import { Injectable, OnDestroy } from '@angular/core';
import { BehaviorSubject, Subscription, Observable } from 'rxjs';
import { BreadcrumbItemModel } from '../models/breadcrumb-item.model';
import { SubheaderModel } from '../models/subheader.model';
import { LayoutService } from './layout.service';

@Injectable({
  providedIn: 'root'
})
export class PageHeaderService implements OnDestroy {
  titleSubject: BehaviorSubject<string> = new BehaviorSubject<string>('Dashboard');
  iconSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  descriptionSubject: BehaviorSubject<string> = new BehaviorSubject<string>('');
  breadCrumbsSubject: BehaviorSubject<BreadcrumbItemModel[]> = new BehaviorSubject<BreadcrumbItemModel[]>([]);

  // private fields
  private unsubscribe: Subscription[] = []; // Read more: => https://brianflove.com/2016/12/11/anguar-2-unsubscribe-observables/

  constructor(private layout: LayoutService) {
  }

  get currentTitle$(): Observable<string> {
    return this.titleSubject.asObservable();
  }

  get currentIcon$(): Observable<string> {
    return this.iconSubject.asObservable();
  }


  setBreadcrumbs(breadcrumbs: BreadcrumbItemModel[] = []) {
    this.breadCrumbsSubject.next(breadcrumbs);
  }

  setTitle(title: string = '') {
    this.titleSubject.next(title);
  }

  setDescription(description: string) {
    this.descriptionSubject.next(description);
  }

  setIcon(icon: string = '') {
    this.iconSubject.next(icon);
  }

  // use this method in SubheaderWrapper
  updateAfterRouteChanges(pathName) {
    const aside = this.getBreadcrumbsAndTitle('sc_aside_menu', pathName);
    const header = this.getBreadcrumbsAndTitle('sc_header_menu', pathName);
    const breadcrumbs =
      aside && aside.breadcrumbs.length > 0
        ? aside.breadcrumbs
        : header.breadcrumbs;

    this.setBreadcrumbs(breadcrumbs);
    this.setTitle(aside?.title ?? header.title);
    this.setIcon(aside?.icon ?? header.icon);
  }

  private getLinksFromMenu(menu): HTMLAnchorElement[] {
    const parentLiElements = Array.from(
      menu.getElementsByClassName('menu-item-open') || []
    ) as HTMLElement[];
    const childLiElements = Array.from(
      menu.getElementsByClassName('menu-item-active') || []
    ) as HTMLElement[];
    const result: HTMLAnchorElement[] = [];
    parentLiElements.forEach((el) => {
      const links = Array.from(
        el.getElementsByClassName('menu-link') || []
      ) as HTMLAnchorElement[];
      if (links && links.length > 0) {
        const aLink = links[0];
        if (aLink.href && aLink.innerHTML !== '/') {
          result.push(aLink);
        }
        
      }
    });

    childLiElements.forEach((el) => {
      const links = Array.from(
        el.getElementsByClassName('menu-link') || []
      ) as HTMLAnchorElement[];
      if (links && links.length > 0) {
        const aLink = links[0];
        if (aLink.href && aLink.innerHTML !== '/') {
          result.push(aLink);
        }
      }
    });
    return result;
  }

  private getBreadcrumbsAndTitle(menuId, pathName): SubheaderModel {
    const result = new SubheaderModel();
    const menu = document.getElementById(menuId);
    if (!menu) {
      return result;
    }

    const activeLinksArray = this.getLinksFromMenu(menu);

    const activeLinks = activeLinksArray.filter((el) => el.tagName === 'A');
    if (!activeLinks) {
      return result;
    }

    activeLinks.forEach((link) => {
      const titleSpans = link.getElementsByClassName('menu-text');
      const iconSpans = link.getElementsByClassName('menu-icon');
      if (titleSpans && iconSpans) {
        const titleSpan = Array.from(titleSpans).find(
          (t) => t.innerHTML && t.innerHTML.trim().length > 0
        );
        const iconSpan = Array.from(iconSpans).find(
          (t) => t.innerHTML && t.innerHTML.trim().length > 0
        );
        if (titleSpan && iconSpan) {
          result.breadcrumbs.push({
            title: titleSpan.innerHTML,
            icon: iconSpan.innerHTML,
            linkPath: link.pathname,
            linkText: titleSpan.innerHTML,
          });
        }
      }
    });
    result.title = this.getTitle(result.breadcrumbs, pathName);
    result.icon = this.getIcon(result.breadcrumbs, pathName);
    return result;
  }

  private getTitle(breadCrumbs, pathname) {
    if (!breadCrumbs || !pathname) {
      return '';
    }

    const length = breadCrumbs.length;
    if (!length) {
      return '';
    }

    return breadCrumbs[length - 1].title;
  }

  private getIcon(breadCrumbs, pathname) {
    if (!breadCrumbs || !pathname) {
      return '';
    }

    const length = breadCrumbs.length;
    if (!length) {
      return '';
    }

    return breadCrumbs[length - 1].icon;
  }
  

  ngOnDestroy() {
    this.unsubscribe.forEach((sb) => sb.unsubscribe());
  }

}
